/**
 * VariantPipelineIntegrationDrawer — per-variant Pipeline Integration
 * configuration drawer (#1191, Slice 2 of meta #1189).
 *
 * The pull-style half of the integration story. Where the trigger-rule
 * drawer configures OttoTester to LISTEN for GitHub deployment_status
 * events, this drawer lets the customer's CI pipeline invoke
 * OttoTester directly — copy YAML / curl / GitLab CI into their repo,
 * authenticate with a personal access token, and the snippet hits
 * `/api/v1/pipelines/runs` (the public endpoint shipped in #1190).
 *
 * **What the drawer renders.**
 *   1. Info callout — tells the operator they'll need a PAT, names
 *      the secret it expects (`OTTOTESTER_API_TOKEN`), and points at
 *      Account Settings → API tokens where the token is generated.
 *   2. Token deep-link affordance — a row with a button-styled link
 *      to `/settings/account/tokens` so the operator can jump there
 *      mid-flow.
 *   3. Form — Suite selection (required, the variant's RunTemplates)
 *      + Target URL strategy (radio: env var / hardcoded / variant
 *      default).
 *   4. Mode tabs — GitHub Actions / curl / GitLab CI. Default:
 *      GitHub Actions.
 *   5. Live-updating snippet block — pure-function output from
 *      `pipelineSnippets.ts`. Copy button + filename header.
 *
 * **URL-state composition.** Slots into the existing
 * `?variantConfig=<vid>&variantConfigKind=...` machinery (#1186);
 * `variantConfigKind=pipeline` opens this drawer instead of the
 * trigger or slack drawer. Round-trip via bookmark + close strips
 * the params, identical to the sibling drawers.
 *
 * **Reuse vs. extract.** The trigger-rules drawer's "Suite selection"
 * picker is embedded inside `TriggerRuleEditorForm` — coupled to the
 * rule editor's form state. Lifting it cleanly would have required
 * splitting a small bit of state out for one consumer; instead we
 * mount `SearchableSelect` directly here (same component the GitHub
 * repo picker uses on the parent tab) and feed it the same CI-pool
 * template list the trigger drawer uses (#2175). Identical visual +
 * identical underlying dataset, two thin consumers — cheaper than a
 * forced extraction.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalLink, Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Label,
  SearchableSelect,
} from '@/components/ui'
import { CopyIconButton } from '@/components/CopyIconButton'
import { useAppContext } from '@/hooks/useAppContext'
import { runTemplatesAPI } from '@/lib/api'
import { track } from '@/lib/telemetry'
import {
  buildPipelineSnippet,
  snippetFilenameFor,
  snippetModeLabel,
  type PipelineSnippetMode,
  type PipelineUrlStrategy,
} from './pipelineSnippets'

/** Display order for the mode tabs. GitHub Actions is the default
 * landing tab — most customers are on GitHub. */
const MODE_TABS: PipelineSnippetMode[] = ['githubActions', 'curl', 'gitlabCi']

/** Display order + label metadata for the URL-strategy radio group.
 * The label / help copy is what the operator reads; the value is
 * what the snippet builder branches on. */
const URL_STRATEGY_OPTIONS: ReadonlyArray<{
  value: PipelineUrlStrategy
  label: string
  help: React.ReactNode
}> = [
  {
    value: 'envVar',
    label: 'From an env var passed by my pipeline',
    help: (
      <>
        Snippet uses <code className="font-mono text-[11px]">$DEPLOY_URL</code>{' '}
        — set it in your workflow before the OttoTester step runs.
      </>
    ),
  },
  {
    value: 'hardcoded',
    label: 'Hardcode a URL',
    help: 'Useful for fixed environments. Type the URL below — it goes into the snippet literally.',
  },
  {
    value: 'variantDefault',
    label: "Use this variant's configured URL",
    help: "Omits the URL override; OttoTester uses the variant's configured URL.",
  },
]

interface RunTemplateLike {
  id?: string
  _id?: string
  name: string
  variantId?: string
}

export interface VariantPipelineIntegrationDrawerProps {
  appId: string
  variantId: string
  onClose: () => void
}

export function VariantPipelineIntegrationDrawer({
  appId,
  variantId,
  onClose,
}: VariantPipelineIntegrationDrawerProps) {
  const { allVariants, workspaceId } = useAppContext()

  const variant = useMemo(
    () => allVariants.find((v) => v.id === variantId) ?? null,
    [allVariants, variantId],
  )

  // Cross-app guard — same shape as the trigger-rules drawer (#1184).
  // A crafted / stale `?variantConfig=<vid>` URL could point at a
  // variant under a different app; without this gate we'd display the
  // wrong app's variant inside this app's context. When the IDs don't
  // match we close; the URL strips back via `onClose`.
  useEffect(() => {
    if (variant && variant.applicationId !== appId) {
      onClose()
    }
  }, [variant, appId, onClose])

  const [templates, setTemplates] = useState<RunTemplateLike[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The CI pool, because that is what this snippet becomes (#2175).
      // The operator pastes it into their pipeline, so the run it starts
      // is a CI run with nobody watching — offering the settings someone
      // edits before a manual run would mean that edit changes what the
      // pipeline does, which is the collision the pools removed.
      const templatesRaw = (await runTemplatesAPI.listForVariant(
        variantId,
        'ci',
      )) as RunTemplateLike[]
      setTemplates(templatesRaw)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suites')
    } finally {
      setLoading(false)
    }
  }, [variantId])

  useEffect(() => {
    void load()
  }, [load])

  // ── Form state ────────────────────────────────────────────────────
  // Template id selection — empty until the operator picks. Snippet
  // renders with a placeholder slot until then.
  const [templateId, setTemplateId] = useState('')
  // Pre-pick the first template once it loads — saves the operator a
  // click in the common case (one suite). Only auto-picks when the
  // selection is still empty so a deliberate "clear" doesn't snap
  // back.
  useEffect(() => {
    if (!templateId && templates.length > 0) {
      const first = templates[0]
      const id = first?.id ?? (first?._id ? String(first._id) : '')
      if (id) setTemplateId(id)
    }
  }, [templates, templateId])

  const [urlStrategy, setUrlStrategy] = useState<PipelineUrlStrategy>('envVar')
  const [hardcodedUrl, setHardcodedUrl] = useState('')
  const [mode, setMode] = useState<PipelineSnippetMode>('githubActions')

  const templateOptions = useMemo(
    () =>
      templates
        .map((t) => ({
          value: t.id ?? (t._id ? String(t._id) : ''),
          label: t.name,
        }))
        .filter((o) => o.value),
    [templates],
  )

  // The API origin the snippet hits. In dev / same-origin deploys
  // this is the page origin; in cross-origin deploys the operator
  // is browsing the OttoTester UI at the same origin as the API
  // anyway. Reading `window.location.origin` at render time is fine
  // because the drawer renders client-side.
  const apiOrigin =
    typeof window !== 'undefined' ? window.location.origin : 'https://app.ottotester.ai'

  const snippetText = useMemo(
    () =>
      buildPipelineSnippet({
        mode,
        urlStrategy,
        workspaceId,
        templateId,
        hardcodedUrl,
        apiOrigin,
      }),
    [mode, urlStrategy, workspaceId, templateId, hardcodedUrl, apiOrigin],
  )

  // Copy affordance lives in the shared `CopyIconButton` (#616 + #1216) —
  // the dark-theme chip shape ships in the primitive itself; the
  // drawer's `onCopied` records the discriminators that matter for
  // analysis (which mode + URL strategy customers actually use).
  const recordCopy = useCallback(() => {
    track('pipeline.snippet.copied', {
      mode,
      urlStrategy,
    })
  }, [mode, urlStrategy])

  const variantName = variant?.name ?? 'Variant'
  const ariaLabel = `Pipeline Integration for ${variantName}`

  return (
    <Drawer open onOpenChange={(open) => !open && onClose()}>
      <DrawerContent
        ariaLabel={ariaLabel}
        data-testid="variant-pipeline-integration-drawer"
      >
        <DrawerHeader
          eyebrow="Pipeline Integration"
          title={variantName}
          subtitle="Pick a suite and a URL strategy. Copy the snippet into your repo."
        />
        <DrawerBody>
          {/* Info callout — names the secret + points at the token
              generation surface. Always visible; the operator needs
              the prerequisite context regardless of fetch state. */}
          <div
            className="mb-3 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900"
            data-testid="drawer-pipeline-callout"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <p className="leading-relaxed">
              <strong className="font-semibold">
                You'll need a personal access token.
              </strong>{' '}
              Stored as{' '}
              <code className="font-mono text-[11px]">OTTOTESTER_API_TOKEN</code>{' '}
              in your repo or org secrets. Scope it to this workspace at
              creation time (Account Settings → API tokens).
            </p>
          </div>

          {/* Token deep-link affordance — surfaced as a dashed-border
              row so the operator scans it as a "go do this first"
              prompt rather than a primary CTA. */}
          <div
            className="mb-4 flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
            data-testid="drawer-pipeline-token-row"
          >
            <strong className="font-semibold text-foreground">Token:</strong>
            <span>Generate one in Account Settings, scoped to this workspace.</span>
            <Link
              to="/settings/account/tokens"
              className="ml-auto inline-flex items-center gap-1 text-primary hover:underline"
              data-testid="drawer-pipeline-token-link"
            >
              Open API tokens <ExternalLink className="h-3 w-3" />
            </Link>
          </div>

          {loading ? (
            <div className="space-y-2" aria-label="Loading suites">
              {[1, 2].map((i) => (
                <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </div>
          ) : (
            <>
              {/* Form section — Suite selection + URL strategy. */}
              <div className="space-y-4 mb-4">
                <div className="space-y-1">
                  <Label htmlFor="pipeline-suite-picker">
                    Suite selection <span className="text-destructive">*</span>
                  </Label>
                  <SearchableSelect
                    id="pipeline-suite-picker"
                    value={templateId}
                    onChange={(v) => setTemplateId(v)}
                    options={templateOptions}
                    placeholder={
                      templateOptions.length === 0
                        ? 'No suites available for this variant'
                        : 'Select a suite'
                    }
                    searchPlaceholder="Search suites…"
                    disabled={templateOptions.length === 0}
                    aria-label="Suite selection"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    The suite that runs when your pipeline fires the snippet.
                  </p>
                </div>

                <div className="space-y-1">
                  <Label>Target URL strategy</Label>
                  <div
                    className="flex flex-col gap-1.5"
                    role="radiogroup"
                    aria-label="Target URL strategy"
                    data-testid="drawer-pipeline-url-strategy"
                  >
                    {URL_STRATEGY_OPTIONS.map((opt) => {
                      const selected = urlStrategy === opt.value
                      return (
                        <label
                          key={opt.value}
                          className={
                            'flex gap-2 rounded-md border px-3 py-2 text-xs cursor-pointer transition-colors ' +
                            (selected
                              ? 'border-primary bg-primary/10'
                              : 'border-border bg-background hover:bg-muted/40')
                          }
                        >
                          <input
                            type="radio"
                            name="pipeline-url-strategy"
                            value={opt.value}
                            checked={selected}
                            onChange={() => setUrlStrategy(opt.value)}
                            className="mt-0.5"
                            data-testid={`drawer-pipeline-url-strategy-${opt.value}`}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-foreground">
                              {opt.label}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {opt.help}
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>

                  {urlStrategy === 'hardcoded' && (
                    <div className="mt-2 space-y-1">
                      <Label htmlFor="pipeline-hardcoded-url">
                        Hardcoded URL
                      </Label>
                      <input
                        id="pipeline-hardcoded-url"
                        type="text"
                        value={hardcodedUrl}
                        onChange={(e) => setHardcodedUrl(e.target.value)}
                        placeholder="https://staging.example.com"
                        className="w-full px-3 py-1.5 text-sm font-mono border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="drawer-pipeline-hardcoded-url-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Folded into the snippet literally. Change it any time —
                        the snippet updates as you type.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Mode tabs — switches which format the snippet block
                  renders. Same call surface; different output. */}
              <div
                className="flex border-b border-border mb-2"
                role="tablist"
                aria-label="Snippet output format"
                data-testid="drawer-pipeline-mode-tabs"
              >
                {MODE_TABS.map((m) => {
                  const selected = mode === m
                  return (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setMode(m)}
                      data-testid={`drawer-pipeline-mode-tab-${m}`}
                      className={
                        'px-3 py-1.5 text-xs border-b-2 transition-colors ' +
                        (selected
                          ? 'border-primary text-primary font-semibold'
                          : 'border-transparent text-muted-foreground hover:text-foreground')
                      }
                    >
                      {snippetModeLabel(m)}
                    </button>
                  )
                })}
              </div>

              {/* Snippet block — dark themed code shell with header
                  + copy. Pre + overflow-x-auto so long lines scroll
                  rather than break the drawer width. */}
              <div className="rounded-md overflow-hidden border border-border bg-[#1f2328]">
                <div className="flex items-center px-3 py-1.5 bg-[#2d333b] border-b border-[#444c56] text-[11px] text-[#adbac7]">
                  <span
                    className="flex-1 font-mono"
                    data-testid="drawer-pipeline-snippet-filename"
                  >
                    {snippetFilenameFor(mode)}
                  </span>
                  <CopyIconButton
                    value={snippetText}
                    label="Copy"
                    ariaLabel="Copy snippet"
                    onCopied={recordCopy}
                    testId="drawer-pipeline-copy-button"
                  />
                </div>
                <pre
                  className="p-3 text-[12px] leading-relaxed text-[#f6f8fa] overflow-x-auto font-mono whitespace-pre"
                  data-testid="drawer-pipeline-snippet-body"
                >
                  {snippetText}
                </pre>
              </div>

              <p className="mt-3 text-[11px] text-muted-foreground">
                Snippet updates live as you change suite, URL strategy, or
                mode. Paste it into your repo, set the{' '}
                <code className="font-mono">OTTOTESTER_API_TOKEN</code> secret,
                and your pipeline can invoke OttoTester.
              </p>
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
